import { useEffect, useState } from 'react'
import { formatPrice } from '@platform/products'
import { Spinner } from '../ui'
import { supabase } from '../../lib/supabase'
import type { SelectedCustomer } from './CustomerSearch'
import type { OrderItem } from './ProductSelector'
import { PhoneNumber } from '../phone/PhoneNumber'
import clsx from 'clsx'

// Schoonhuisshop verzendregel: €6,50 onder €75, gratis boven
const SHIPPING_COST_CENTS = 650
const FREE_SHIPPING_THRESHOLD_CENTS = 7500

export type KostenMode = 'gratis' | 'korting' | 'doorberekenen'

interface Props {
  customer: SelectedCustomer
  items: OrderItem[]
  shippingMethod: 'postnl' | 'chauffeur'
  onShippingMethodChange: (method: 'postnl' | 'chauffeur') => void
  shippingCost: number
  onShippingCostChange: (cost: number) => void
  reason: string
  onReasonChange: (reason: string) => void
  brand: 'KLEDA' | 'SCHOONHUISSHOP'
  onBrandChange: (brand: 'KLEDA' | 'SCHOONHUISSHOP') => void
  kostenMode: KostenMode
  onKostenModeChange: (mode: KostenMode) => void
  discountPercent: number
  onDiscountPercentChange: (pct: number) => void
  bevestigingenSturen: boolean
  onBevestigingenSturenChange: (send: boolean) => void
  notes: string
  onNotesChange: (notes: string) => void
  onSuccess: (werkOrderId: string, werkOrderName: string, warning?: string) => void
}

const reasons = [
  { value: 'coulance', label: 'Coulance' },
  { value: 'garantie', label: 'Garantie' },
  { value: 'nabestelling', label: 'Nabestelling' },
  { value: 'cadeau', label: 'Cadeau' },
  { value: 'handmatig', label: 'Handmatige bestelling' },
]

export function OrderSummary({
  customer, items, shippingMethod, onShippingMethodChange,
  shippingCost, onShippingCostChange,
  reason, onReasonChange, brand, onBrandChange,
  kostenMode, onKostenModeChange,
  discountPercent, onDiscountPercentChange,
  bevestigingenSturen, onBevestigingenSturenChange,
  notes, onNotesChange, onSuccess,
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const subtotal = items.reduce((sum, i) => sum + i.priceInclBtw * i.quantity, 0)

  // 3 standen: gratis (geen kosten), korting (kosten − %) of doorberekenen (volledig).
  const chargeCustomer = kostenMode !== 'gratis'
  const discountPct = kostenMode === 'korting' ? Math.min(100, Math.max(0, discountPercent)) : 0
  const discountAmount = Math.round((subtotal * discountPct) / 100)
  const discountedSubtotal = subtotal - discountAmount

  // Auto-bereken verzendkosten: PostNL + kosten → €6,50 (gratis boven €75 op het te
  // betalen bedrag). Anders €0.
  const suggestedShipping =
    shippingMethod === 'postnl' && chargeCustomer && discountedSubtotal < FREE_SHIPPING_THRESHOLD_CENTS
      ? SHIPPING_COST_CENTS
      : 0

  useEffect(() => {
    onShippingCostChange(suggestedShipping)
  }, [suggestedShipping, onShippingCostChange])

  const effectiveShipping = chargeCustomer ? shippingCost : 0
  const total = (chargeCustomer ? discountedSubtotal : 0) + effectiveShipping

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setError(null)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Niet ingelogd')

      const response = await fetch('/api/create-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          accountId: customer.accountId || undefined,
          customer: customer.isNew ? {
            firstName: customer.name.split(' ')[0] || '',
            lastName: customer.name.split(' ').slice(1).join(' ') || customer.name,
            email: customer.email,
            phone: customer.phone,
            street: customer.street,
            postalCode: customer.postalCode,
            city: customer.city,
          } : undefined,
          items: items.map((i) => ({
            productId: i.productId,
            name: i.name,
            sfProductCode: i.sfProductCode,
            quantity: i.quantity,
            priceInclBtw: i.priceInclBtw,
            weight: i.weight,
            customSize: i.customSize,
          })),
          shippingMethod,
          shippingCost: effectiveShipping,
          reason,
          brand,
          notes: notes || undefined,
          chargeCustomer,
          discountPercent: discountPct,
          bevestigingenSturen,
        }),
      })

      const text = await response.text()
      let data: Record<string, unknown>
      try {
        data = JSON.parse(text)
      } catch {
        throw new Error(`Server error (${response.status}): ${text.slice(0, 200)}`)
      }

      if (!response.ok) {
        throw new Error((data.error as string) || (data.message as string) || `Server error ${response.status}`)
      }

      onSuccess(data.werkOrderId as string, data.werkOrderName as string, data.warning as string | undefined)
    } catch (err) {
      console.error('Create order error:', err)
      setError(err instanceof Error ? err.message : 'Onbekende fout')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Shipping & options */}
      <div className="grid grid-cols-2 gap-6">
        {/* Left: options */}
        <div className="space-y-4">
          {/* Shipping method */}
          <div>
            <label className="text-sm font-semibold text-main block mb-2">Verzendmethode</label>
            <div className="flex gap-2">
              <button
                onClick={() => onShippingMethodChange('postnl')}
                className={clsx(
                  'flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors',
                  shippingMethod === 'postnl'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                )}
              >
                PostNL
              </button>
              <button
                onClick={() => onShippingMethodChange('chauffeur')}
                className={clsx(
                  'flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors',
                  shippingMethod === 'chauffeur'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                )}
              >
                Chauffeur
              </button>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="text-sm font-semibold text-main block mb-2">Reden</label>
            <select
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {reasons.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          {/* Brand */}
          <div>
            <label className="text-sm font-semibold text-main block mb-2">Merk</label>
            <div className="flex gap-2">
              <button
                onClick={() => onBrandChange('KLEDA')}
                className={clsx(
                  'flex-1 px-4 py-2 rounded-lg text-sm font-medium border transition-colors',
                  brand === 'KLEDA'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                )}
              >
                Kleda
              </button>
              <button
                onClick={() => onBrandChange('SCHOONHUISSHOP')}
                className={clsx(
                  'flex-1 px-4 py-2 rounded-lg text-sm font-medium border transition-colors',
                  brand === 'SCHOONHUISSHOP'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                )}
              >
                Schoonhuisshop
              </button>
            </div>
          </div>

          {/* Charge toggle: gratis / korting / doorberekenen */}
          <div>
            <label className="text-sm font-semibold text-main block mb-2">Kosten</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => onKostenModeChange('gratis')}
                className={clsx(
                  'px-3 py-2 rounded-lg text-sm font-medium border transition-colors',
                  kostenMode === 'gratis'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                )}
              >
                Gratis
              </button>
              <button
                onClick={() => onKostenModeChange('korting')}
                className={clsx(
                  'px-3 py-2 rounded-lg text-sm font-medium border transition-colors',
                  kostenMode === 'korting'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                )}
              >
                Korting
              </button>
              <button
                onClick={() => onKostenModeChange('doorberekenen')}
                className={clsx(
                  'px-3 py-2 rounded-lg text-sm font-medium border transition-colors',
                  kostenMode === 'doorberekenen'
                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                )}
              >
                Doorberekenen
              </button>
            </div>
          </div>

          {/* Kortingspercentage */}
          {kostenMode === 'korting' && (
            <div>
              <label className="text-sm font-semibold text-main block mb-2">Kortingspercentage</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={discountPercent}
                  onChange={(e) => {
                    const pct = Math.min(100, Math.max(0, Math.round(parseFloat(e.target.value) || 0)))
                    onDiscountPercentChange(pct)
                  }}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <span className="text-sm text-muted">%</span>
                {discountAmount > 0 && (
                  <span className="text-sm text-muted">
                    − {formatPrice(discountAmount)} → klant betaalt {formatPrice(total)}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Shipping cost */}
          {chargeCustomer && (
            <div>
              <label className="text-sm font-semibold text-main block mb-2">
                Verzendkosten
                {shippingMethod === 'postnl' && (
                  <span className="text-muted font-normal ml-1">
                    (webshop-regel: {formatPrice(SHIPPING_COST_CENTS)}, gratis boven {formatPrice(FREE_SHIPPING_THRESHOLD_CENTS)})
                  </span>
                )}
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted">€</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={(shippingCost / 100).toFixed(2)}
                  onChange={(e) => {
                    const euros = parseFloat(e.target.value) || 0
                    onShippingCostChange(Math.round(euros * 100))
                  }}
                  className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                {shippingCost !== suggestedShipping && (
                  <button
                    onClick={() => onShippingCostChange(suggestedShipping)}
                    className="text-xs text-indigo-600 hover:text-indigo-700 underline"
                  >
                    Reset naar {formatPrice(suggestedShipping)}
                  </button>
                )}
              </div>
              {shippingMethod === 'chauffeur' && shippingCost > 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  Let op: verzendkosten bij chauffeur — meestal gratis bij eigen rit.
                </p>
              )}
            </div>
          )}

          {/* Bevestigingen sturen */}
          <div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={bevestigingenSturen}
                onChange={(e) => onBevestigingenSturenChange(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span>
                <span className="text-sm font-semibold text-main block">Bevestigingsmail sturen</span>
                <span className="text-xs text-muted">
                  Verstuurt automatisch order- en verzendbevestiging zodra de status verandert. Uitvinken bij interne / coulance bestellingen waar de klant niets hoeft te ontvangen.
                </span>
              </span>
            </label>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-semibold text-main block mb-2">
              Opmerkingen <span className="text-muted font-normal">(optioneel)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            />
          </div>
        </div>

        {/* Right: review */}
        <div className="space-y-4">
          {/* Customer */}
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">Klant</h4>
            <p className="text-sm font-medium text-main">{customer.name}</p>
            {customer.email && <p className="text-xs text-muted">{customer.email}</p>}
            {customer.phone && <p className="text-xs text-muted"><PhoneNumber number={customer.phone} customerName={customer.name} sourceContext="nalevering-summary" /></p>}
            {customer.street && (
              <p className="text-xs text-muted mt-1">{customer.street}, {customer.postalCode} {customer.city}</p>
            )}
          </div>

          {/* Items */}
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">Producten</h4>
            <div className="space-y-2">
              {items.map((item) => (
                <div key={item.lineId} className="flex justify-between text-sm">
                  <span className="text-main">
                    {item.quantity}× {item.name}
                    {item.customSize && (
                      <span className="text-muted"> ({item.customSize.width}×{item.customSize.length}cm)</span>
                    )}
                  </span>
                  <span className="font-medium text-main">
                    {chargeCustomer ? formatPrice(item.priceInclBtw * item.quantity) : 'Gratis'}
                  </span>
                </div>
              ))}
            </div>
            {chargeCustomer && (
              <div className="border-t border-slate-200 mt-3 pt-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-muted">Subtotaal</span>
                  <span className="text-main">{formatPrice(subtotal)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted">Korting ({discountPct}%)</span>
                    <span className="text-emerald-600">− {formatPrice(discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted">Verzendkosten</span>
                  <span className="text-main">
                    {effectiveShipping === 0 ? 'Gratis' : formatPrice(effectiveShipping)}
                  </span>
                </div>
                <div className="flex justify-between pt-1 border-t border-slate-200">
                  <span className="text-sm font-semibold text-main">Totaal</span>
                  <span className="text-sm font-semibold text-main">{formatPrice(total)}</span>
                </div>
              </div>
            )}
            {!chargeCustomer && (
              <div className="border-t border-slate-200 mt-3 pt-3 flex justify-between">
                <span className="text-sm font-semibold text-main">Totaal</span>
                <span className="text-sm font-semibold text-main">Gratis</span>
              </div>
            )}
          </div>

          {/* Summary badges */}
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
              {shippingMethod === 'postnl' ? 'PostNL' : 'Chauffeur'}
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
              {reasons.find((r) => r.value === reason)?.label}
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
              {brand}
            </span>
            {!chargeCustomer && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                Gratis
              </span>
            )}
            {kostenMode === 'korting' && discountPct > 0 && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                Korting {discountPct}%
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Doorberekenen-waarschuwing — voorkomt dat een nabestelling per ongeluk
          een betaalverzoek naar de klant stuurt (zie create-order betaalflow). */}
      {chargeCustomer && total > 0 && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <span>
            <strong>De klant ontvangt een betaalverzoek van {formatPrice(total)}.</strong>{' '}
            Er wordt een betaallink aangemaakt en een bevestigingsmail met betaalverzoek verstuurd.
            Is dit een gratis nabestelling of coulance? Zet <strong>Kosten</strong> hierboven dan op{' '}
            <strong>Gratis</strong>.
          </span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Submit */}
      <div className="flex justify-end pt-2">
        <button
          onClick={() => handleSubmit()}
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {isSubmitting ? <Spinner className="w-4 h-4" /> : null}
          {isSubmitting ? 'Bezig met aanmaken...' : 'Bestelling aanmaken'}
        </button>
      </div>
    </div>
  )
}
